import { useMemo } from "react";

type TableRecord = Record<string, any>;

type RowAction = {
  key: string;
  label: string;
  icon: string;
  ariaLabel: string;
  danger?: boolean;
  onClick: (row: TableRecord) => void;
};

export function useGridRowActions({
  canDelete,
  canEdit,
  isTvaTable,
  isEnergyTable,
  isLocaleTable,
  isEnvironmentTable,
  isAdminUsersTable,
  isAccessoryGroupTable,
  isYearLossTable,
  isAnnexTable,
  isFaqTable,
  isVersionsTable,
  isSatisfactionTable,
  isParameterTable,
  isDiscountManagementTable,
  isAccessoryEditTable,
  isAccessorySuperTable,
  isHpSuperTable,
  isHpTable,
  isTutorialsLayout,
  canOpenTvaEdit,
  canOpenEnergyEdit,
  canOpenLocaleEdit,
  canOpenEnvironmentEdit,
  canOpenAdminUsersEdit,
  canOpenAccessoryGroupEdit,
  canOpenYearLossEdit,
  canOpenAnnexEdit,
  canOpenFaqEdit,
  canOpenVersionEdit,
  canOpenSatisfactionView,
  canOpenParameterEdit,
  canOpenDiscountManagementEdit,
  canOpenMenuDefEdit,
  canOpenAccessoryEdit,
  canOpenAccessorySuperEdit,
  canOpenHpSuperEdit,
  canOpenHpEdit,
  onOpenTvaEdit,
  onOpenEnergyEdit,
  onOpenLocaleEdit,
  onOpenEnvironmentEdit,
  onOpenAdminUsersEdit,
  onOpenAccessoryGroupEdit,
  onOpenYearLossEdit,
  onOpenAnnexEdit,
  onOpenFaqEdit,
  onOpenVersionEdit,
  onOpenSatisfactionView,
  onOpenParameterEdit,
  onOpenDiscountManagementEdit,
  onOpenMenuDefEdit,
  onOpenAccessoryEdit,
  onOpenAccessorySuperEdit,
  onOpenHpSuperEdit,
  onOpenHpEdit,
  onOpenTutorialEdit,
  onDelete,
  layoutId,
  tableWidgetTable,
  tableTitle,
}: {
  canDelete: boolean;
  canEdit: boolean;
  isTvaTable: boolean;
  isEnergyTable: boolean;
  isLocaleTable: boolean;
  isEnvironmentTable: boolean;
  isAdminUsersTable: boolean;
  isAccessoryGroupTable: boolean;
  isYearLossTable: boolean;
  isAnnexTable: boolean;
  isFaqTable: boolean;
  isVersionsTable: boolean;
  isSatisfactionTable: boolean;
  isParameterTable: boolean;
  isDiscountManagementTable: boolean;
  isAccessoryEditTable: boolean;
  isAccessorySuperTable: boolean;
  isHpSuperTable: boolean;
  isHpTable: boolean;
  isTutorialsLayout: boolean;
  canOpenTvaEdit: boolean;
  canOpenEnergyEdit: boolean;
  canOpenLocaleEdit: boolean;
  canOpenEnvironmentEdit: boolean;
  canOpenAdminUsersEdit: boolean;
  canOpenAccessoryGroupEdit: boolean;
  canOpenYearLossEdit: boolean;
  canOpenAnnexEdit: boolean;
  canOpenFaqEdit: boolean;
  canOpenVersionEdit: boolean;
  canOpenSatisfactionView: boolean;
  canOpenParameterEdit: boolean;
  canOpenDiscountManagementEdit: boolean;
  canOpenMenuDefEdit: boolean;
  canOpenAccessoryEdit: boolean;
  canOpenAccessorySuperEdit: boolean;
  canOpenHpSuperEdit: boolean;
  canOpenHpEdit: boolean;
  onOpenTvaEdit: (row: TableRecord) => void;
  onOpenEnergyEdit: (row: TableRecord) => void;
  onOpenLocaleEdit: (row: TableRecord) => void;
  onOpenEnvironmentEdit: (row: TableRecord) => void;
  onOpenAdminUsersEdit: (row: TableRecord) => void;
  onOpenAccessoryGroupEdit: (row: TableRecord) => void;
  onOpenYearLossEdit: (row: TableRecord) => void;
  onOpenAnnexEdit: (row: TableRecord) => void;
  onOpenFaqEdit: (row: TableRecord) => void;
  onOpenVersionEdit: (row: TableRecord) => void;
  onOpenSatisfactionView: (row: TableRecord) => void;
  onOpenParameterEdit: (row: TableRecord) => void;
  onOpenDiscountManagementEdit: (row: TableRecord) => void;
  onOpenMenuDefEdit: (row: TableRecord) => void;
  onOpenAccessoryEdit: (row: TableRecord) => void;
  onOpenAccessorySuperEdit: (row: TableRecord) => void;
  onOpenHpSuperEdit: (row: TableRecord) => void;
  onOpenHpEdit: (row: TableRecord) => void;
  onOpenTutorialEdit: (row: TableRecord) => void;
  onDelete: (row: TableRecord) => void;
  layoutId?: string;
  tableWidgetTable?: unknown;
  tableTitle: string;
}) {
  return useMemo(() => {
    const actions: RowAction[] = [];

    if (canEdit || isTvaTable || isEnergyTable || isLocaleTable || isEnvironmentTable || isAdminUsersTable || isAccessoryGroupTable || isYearLossTable || isAnnexTable || isFaqTable || isVersionsTable || isSatisfactionTable || isParameterTable || isDiscountManagementTable || isAccessoryEditTable || isAccessorySuperTable || isHpSuperTable || isHpTable || isTutorialsLayout) {
      actions.push({
        key: "edit",
        label: "Edit",
        icon: "edit",
        ariaLabel: "Edit row",
        onClick: (row: TableRecord) => {
          if (canOpenYearLossEdit) {
            onOpenYearLossEdit(row);
            return;
          }

          if (canOpenAccessoryGroupEdit) {
            onOpenAccessoryGroupEdit(row);
            return;
          }

          if (canOpenLocaleEdit) {
            onOpenLocaleEdit(row);
            return;
          }

          if (canOpenEnvironmentEdit) {
            onOpenEnvironmentEdit(row);
            return;
          }

          if (canOpenAdminUsersEdit) {
            onOpenAdminUsersEdit(row);
            return;
          }

          if (canOpenEnergyEdit) {
            onOpenEnergyEdit(row);
            return;
          }

          if (canOpenTvaEdit) {
            onOpenTvaEdit(row);
            return;
          }

          if (canOpenAnnexEdit) {
            onOpenAnnexEdit(row);
            return;
          }

          if (canOpenFaqEdit || canOpenVersionEdit || canOpenSatisfactionView) {
            if (canOpenFaqEdit) {
              onOpenFaqEdit(row);
            } else if (canOpenVersionEdit) {
              onOpenVersionEdit(row);
            } else {
              onOpenSatisfactionView(row);
            }
            return;
          }

          if (canOpenParameterEdit) {
            onOpenParameterEdit(row);
            return;
          }

          if (canOpenDiscountManagementEdit) {
            onOpenDiscountManagementEdit(row);
            return;
          }

          if (canOpenMenuDefEdit) {
            onOpenMenuDefEdit(row);
            return;
          }

          if (canOpenAccessoryEdit) {
            onOpenAccessoryEdit(row);
            return;
          }

          if (canOpenAccessorySuperEdit) {
            onOpenAccessorySuperEdit(row);
            return;
          }

          if (canOpenHpSuperEdit) {
            onOpenHpSuperEdit(row);
            return;
          }

          if (canOpenHpEdit || isTutorialsLayout) {
            if (canOpenHpEdit) {
              onOpenHpEdit(row);
            } else {
              onOpenTutorialEdit(row);
            }
            return;
          }

          console.debug("Edit action requested", {
            layoutId,
            table: tableWidgetTable,
            title: tableTitle,
            rowId: row.id,
            row,
          });
        },
      });
    }

    if (canDelete) {
      actions.push({
        key: "delete",
        label: "Delete",
        icon: "delete",
        ariaLabel: "Delete row",
        onClick: (row: TableRecord) => {
          onDelete(row);
        },
      });
    }

    return actions;
  }, [
    canDelete,
    canEdit,
    canOpenAccessoryGroupEdit,
    canOpenEnergyEdit,
    canOpenEnvironmentEdit,
    canOpenAdminUsersEdit,
    canOpenLocaleEdit,
    canOpenTvaEdit,
    canOpenYearLossEdit,
    canOpenAnnexEdit,
    canOpenFaqEdit,
    canOpenVersionEdit,
    canOpenSatisfactionView,
    canOpenParameterEdit,
    canOpenDiscountManagementEdit,
    canOpenMenuDefEdit,
    canOpenAccessoryEdit,
    canOpenAccessorySuperEdit,
    canOpenHpSuperEdit,
    canOpenHpEdit,
    isAccessoryGroupTable,
    isEnergyTable,
    isEnvironmentTable,
    isAdminUsersTable,
    isLocaleTable,
    isTvaTable,
    isYearLossTable,
    isAnnexTable,
    isFaqTable,
    isVersionsTable,
    isSatisfactionTable,
    isParameterTable,
    isDiscountManagementTable,
    isAccessoryEditTable,
    isAccessorySuperTable,
    isHpSuperTable,
    isHpTable,
    isTutorialsLayout,
    layoutId,
    onDelete,
    onOpenAccessoryGroupEdit,
    onOpenEnergyEdit,
    onOpenEnvironmentEdit,
    onOpenAdminUsersEdit,
    onOpenLocaleEdit,
    onOpenTvaEdit,
    onOpenYearLossEdit,
    onOpenAnnexEdit,
    onOpenFaqEdit,
    onOpenVersionEdit,
    onOpenSatisfactionView,
    onOpenParameterEdit,
    onOpenDiscountManagementEdit,
    onOpenMenuDefEdit,
    onOpenAccessoryEdit,
    onOpenAccessorySuperEdit,
    onOpenHpSuperEdit,
    onOpenHpEdit,
    onOpenTutorialEdit,
    tableTitle,
    tableWidgetTable,
  ]);
}
